import { CREDIT_API_URL } from '../constants';
import showToast from '../utils/toast';

/**
 * Anchor Service - API calls for Anchor Dashboard
 */

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const handleUnauthorized = async (response) => {
  if (response.status === 401 || response.status === 403) {
    // Clear session first
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userInfo');
    localStorage.removeItem('token');
    
    // Show toast notification
    showToast('Session expired. Please login again.', 'warning', 2000);
    
    // Wait for toast to be visible before redirecting
    setTimeout(() => {
      window.location.href = '/';
    }, 1500);
    
    throw new Error('Unauthorized');
  }
};

const anchorService = {
  // Dashboard Metrics
  getDashboardMetrics: async (anchorId) => {
    const response = await fetch(`${CREDIT_API_URL}anchor/${anchorId}/dashboard/metrics`, {
      headers: getAuthHeaders()
    });
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to fetch dashboard metrics');
    return response.json();
  },

  // Invoice Management
  getInvoices: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || 20,
      ...(params.status && { status: params.status }),
      ...(params.dateFrom && { dateFrom: params.dateFrom }),
      ...(params.dateTo && { dateTo: params.dateTo }),
      ...(params.amountMin && { amountMin: params.amountMin }),
      ...(params.amountMax && { amountMax: params.amountMax }),
      ...(params.supplierId && { supplierId: params.supplierId }),
      ...(params.sortBy && { sortBy: params.sortBy }),
      ...(params.search && { search: params.search })
    });

    const response = await fetch(
      `${CREDIT_API_URL}anchor/invoices?${queryParams}`,
      { headers: getAuthHeaders() }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to fetch invoices');
    return response.json();
  },

  getInvoiceDetails: async (invoiceId) => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/invoices/${invoiceId}`,
      { headers: getAuthHeaders() }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to fetch invoice details');
    return response.json();
  },

  // NEW UNIFIED DECISION API
  updateInvoiceDecision: async (invoiceId, data) => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/invoices/${invoiceId}/decision`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to update invoice decision');
    return response.json();
  },

  // Accept Invoice (uses unified decision API)
  approveInvoice: async (invoiceId, data) => {
    return anchorService.updateInvoiceDecision(invoiceId, {
      buyerDecision: 'Accepted',
      approvedAmount: data.approvedAmount,
      remarks: data.remarks
    });
  },

  // Reject Invoice (uses unified decision API)
  rejectInvoice: async (invoiceId, data) => {
    return anchorService.updateInvoiceDecision(invoiceId, {
      buyerDecision: 'Rejected',
      rejectionReason: data.rejectionReason
    });
  },

  // Add/Update Remarks (uses unified decision API)
  updateInvoiceRemarks: async (invoiceId, data) => {
    return anchorService.updateInvoiceDecision(invoiceId, {
      remarks: data.remarks
    });
  },

  bulkApproveInvoices: async (invoiceIds, remarks = '') => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/invoices/bulk-approve`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ invoiceIds, remarks })
      }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to bulk approve invoices');
    return response.json();
  },

  bulkRejectInvoices: async (invoiceIds, reason, remarks) => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/invoices/bulk-reject`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ invoiceIds, reason, remarks })
      }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to bulk reject invoices');
    return response.json();
  },

  // Supplier Management
  getSuppliers: async (anchorId, search = '') => {
    const queryParams = new URLSearchParams({
      ...(search && { search })
    });
    
    const response = await fetch(
      `${CREDIT_API_URL}anchor/${anchorId}/suppliers?${queryParams}`,
      { headers: getAuthHeaders() }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to fetch suppliers');
    return response.json();
  },

  getSupplierInvoices: async (anchorId, supplierId) => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/${anchorId}/suppliers/${supplierId}/invoices`,
      { headers: getAuthHeaders() }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to fetch supplier invoices');
    return response.json();
  },

  // Analytics
  getAnalytics: async (anchorId, period = '6months') => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/${anchorId}/analytics?period=${period}`,
      { headers: getAuthHeaders() }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to fetch analytics');
    return response.json();
  },

  // Reports
  getMonthlyReport: async (anchorId, month, year) => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/${anchorId}/reports/monthly?month=${month}&year=${year}`,
      { headers: getAuthHeaders() }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to fetch monthly report');
    return response.json();
  },

  exportReport: async (anchorId, type = 'excel', filters = {}) => {
    const queryParams = new URLSearchParams({
      type,
      ...filters
    });
    
    const response = await fetch(
      `${CREDIT_API_URL}anchor/${anchorId}/reports/export?${queryParams}`,
      { headers: getAuthHeaders() }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to export report');
    return response.blob();
  },

  // Notifications
  getNotifications: async (anchorId) => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/${anchorId}/notifications`,
      { headers: getAuthHeaders() }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to fetch notifications');
    return response.json();
  },

  markNotificationAsRead: async (notificationId) => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/notifications/${notificationId}/read`,
      {
        method: 'PUT',
        headers: getAuthHeaders()
      }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to mark notification as read');
    return response.json();
  },

  // Anchor Profile
  getAnchorProfile: async (anchorId) => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/${anchorId}`,
      { headers: getAuthHeaders() }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to fetch anchor profile');
    return response.json();
  },

  updateAnchorProfile: async (anchorId, data) => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/${anchorId}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    await handleUnauthorized(response);
    if (!response.ok) throw new Error('Failed to update anchor profile');
    return response.json();
  }
};

export default anchorService;

